import React, { useState, useEffect } from 'react'
import {
    Box,
    TextField,
    Button,
    Card,
    CardContent,
    Typography,
    Grid,
    MenuItem,
    CircularProgress,
    Alert,
    Divider,
    FormControl,
    InputLabel,
    Select,
    Chip,
    Collapse,
} from '@mui/material'
import { Search, Save, ExpandMore, ExpandLess } from '@mui/icons-material'
import axiosInstance from '../../services/axiosInstance'
import { fetchEnabledTenants } from '../../services/tenantService'
import { fetchCantonesWithTenants } from '../../services/cantonService'
import { getParishAll, getSitesByParish } from '../../services/index'

const TreeReassignment = () => {
    const [searchTerm, setSearchTerm] = useState('')
    const [loading, setLoading] = useState(false)
    const [tree, setTree] = useState(null)
    const [error, setError] = useState(null)
    const [success, setSuccess] = useState(null)

    // Selection states
    const [tenants, setTenants] = useState([])
    const [cantons, setCantons] = useState([])
    const [parishes, setParishes] = useState([])
    const [sites, setSites] = useState([])

    const [filteredCantons, setFilteredCantons] = useState([])

    // Use empty string as default to keep controlled
    const [selectedTenant, setSelectedTenant] = useState('')
    const [selectedCanton, setSelectedCanton] = useState('')
    const [selectedParish, setSelectedParish] = useState('')
    const [selectedSite, setSelectedSite] = useState('')

    const [assigning, setAssigning] = useState(false)
    const [showLocationChange, setShowLocationChange] = useState(false)

    // Initial Data Fetch
    useEffect(() => {
        const initData = async () => {
            try {
                const [tenantsData, cantonsData] = await Promise.all([
                    fetchEnabledTenants(),
                    fetchCantonesWithTenants(),
                ])
                setTenants(tenantsData || [])
                setCantons(cantonsData || [])
            } catch (err) {
                console.error('Error fetching initial data:', err)
            }
        }
        initData()
    }, [])

    const handleSearch = async () => {
        if (!searchTerm) return
        setLoading(true)
        setError(null)
        setTree(null)
        setSuccess(null)
        // Reset selection - use empty string not undefined
        setSelectedTenant('')
        setSelectedCanton('')
        setSelectedParish('')
        setSelectedSite('')
        setShowLocationChange(false)
        setParishes([])
        setSites([])
        setFilteredCantons([])

        try {
            console.log('Searching for:', searchTerm)
            const response = await axiosInstance.get('/tree/search-by-identifier', {
                params: { identifier: searchTerm },
            })
            console.log('Search response:', response)
            if (response.data && response.data.tree) {
                setTree(response.data.tree)
            } else {
                setError('No se encontro informacion del arbol (Respuesta vacia)')
            }
        } catch (err) {
            console.error('Search error:', err)
            const errorMsg = err.response?.data?.message || err.message || 'Error al buscar el arbol'
            setError(errorMsg)
        } finally {
            setLoading(false)
        }
    }

    // Handle Tenant Change (for reassignment)
    const handleTenantChange = (e) => {
        const tenantId = e.target.value
        setSelectedTenant(tenantId || '')
        // Reset location fields when tenant changes
        setSelectedCanton('')
        setSelectedParish('')
        setSelectedSite('')
        setParishes([])
        setSites([])

        // Filter Cantons that belong to this Tenant
        if (tenantId) {
            const selectedTenantObj = tenants.find(t => t.id === tenantId)
            const tenantSlug = selectedTenantObj?.slug

            if (tenantSlug) {
                const filtered = cantons.filter(c => {
                    if (c.tenant_slug === tenantSlug) return true
                    if (c.tenants && Array.isArray(c.tenants)) {
                        return c.tenants.some(t => t.slug === tenantSlug)
                    }
                    return false
                })
                setFilteredCantons(filtered)
            } else {
                setFilteredCantons([])
            }
        } else {
            setFilteredCantons([])
        }
    }

    // Handle Canton Change
    const handleCantonChange = async (e) => {
        const cantonSlug = e.target.value
        setSelectedCanton(cantonSlug || '')
        setSelectedParish('')
        setSelectedSite('')
        setSites([])

        if (cantonSlug) {
            try {
                const parishesData = await getParishAll(cantonSlug)
                if (parishesData && typeof parishesData === 'object') {
                    const formatted = Object.entries(parishesData).map(([id, name]) => ({ id, name }))
                    setParishes(formatted)
                } else {
                    setParishes([])
                }
            } catch (err) {
                console.error('Error fetching parishes:', err)
                setParishes([])
            }
        } else {
            setParishes([])
        }
    }

    // Handle Parish Change - Now uses getSitesByParish endpoint
    const handleParishChange = async (e) => {
        const parishId = e.target.value
        setSelectedParish(parishId || '')
        setSelectedSite('')

        if (parishId) {
            try {
                const sitesData = await getSitesByParish(parishId)
                setSites(sitesData || [])
            } catch (err) {
                console.error('Error fetching sites:', err)
                setSites([])
            }
        } else {
            setSites([])
        }
    }

    const handleAssign = async () => {
        if (!tree) return

        // Validate: at least tenant or site must be selected
        if (!selectedTenant && !selectedSite) {
            setError('Debe seleccionar al menos un nuevo Tenant o un nuevo Sitio')
            return
        }

        setAssigning(true)
        setError(null)
        setSuccess(null)

        try {
            const payload = {}

            // Add tenantId if a new tenant was selected
            if (selectedTenant) {
                payload.tenantId = selectedTenant
            }

            // Add newSiteId only if location was changed
            if (selectedSite) {
                payload.newSiteId = selectedSite
            }

            console.log('Reassigning with payload:', payload)

            await axiosInstance.put(`/tree/reassign/${tree.id_a}`, payload)
            setSuccess('El arbol ha sido reasignado correctamente.')

            // Refresh tree data
            handleSearch()
        } catch (err) {
            console.error('Reassign error:', err)
            setError(err.response?.data?.message || 'Error al reasignar el arbol')
        } finally {
            setAssigning(false)
        }
    }

    // Get current tenant info from tree
    const getCurrentTenant = () => {
        if (tree?.Tenant) {
            return tree.Tenant.nombre || tree.Tenant.nombre_completo || tree.Tenant.slug
        }
        return 'Sin asignar'
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
                <Grid item xs={12} md={8}>
                    <TextField
                        fullWidth
                        label="Buscar por Codigo o UUID"
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder="Ej: LOJ.123 o 550e8400-e29b..."
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Search />}
                        onClick={handleSearch}
                        disabled={loading || !searchTerm}
                        fullWidth
                        size="large"
                        sx={{ height: 56 }}
                    >
                        Buscar
                    </Button>
                </Grid>
            </Grid>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {tree && (
                <Card sx={{ mt: 3, boxShadow: 3 }}>
                    <CardContent>
                        <Grid container spacing={3}>
                            {/* Current Info Section */}
                            <Grid item xs={12} md={5}>
                                <Typography variant="h6" gutterBottom color="primary.main">
                                    Informacion Actual
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Typography><strong>Codigo:</strong> {tree.codigo}</Typography>
                                    <Typography><strong>Especie:</strong> {tree.Specie?.nombre_cientifico || 'N/A'}</Typography>
                                    <Typography><strong>Nombre Comun:</strong> {tree.Specie?.PseudonymModels?.[0]?.nombre_comun || 'N/A'}</Typography>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography><strong>Tenant:</strong></Typography>
                                        <Chip
                                            label={getCurrentTenant()}
                                            color={tree?.Tenant ? 'primary' : 'default'}
                                            size="small"
                                            variant={tree?.Tenant ? 'filled' : 'outlined'}
                                        />
                                    </Box>
                                    <Typography><strong>Canton:</strong> {tree.Site?.parroquia?.canton?.nombre || 'N/A'}</Typography>
                                    <Typography><strong>Parroquia:</strong> {tree.Site?.parroquia?.nombre || 'N/A'}</Typography>
                                    <Typography><strong>Sitio:</strong> {tree.Site?.nombre || 'N/A'}</Typography>
                                </Box>
                            </Grid>

                            {/* Reassignment Section */}
                            <Grid item xs={12} md={7}>
                                <Typography variant="h6" gutterBottom color="secondary.main">
                                    Reasignar Arbol
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                <Grid container spacing={2}>
                                    {/* Primary Action: Select New Tenant */}
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel id="tenant-select-label">Nuevo Tenant *</InputLabel>
                                            <Select
                                                labelId="tenant-select-label"
                                                value={selectedTenant}
                                                label="Nuevo Tenant *"
                                                onChange={handleTenantChange}
                                            >
                                                <MenuItem value="">
                                                    <em>Seleccionar tenant...</em>
                                                </MenuItem>
                                                {tenants.map((t, index) => (
                                                    <MenuItem key={`tenant-${t.id || index}`} value={t.id || ''}>
                                                        {t.name || t.fullName || t.slug || 'Sin nombre'}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {/* Toggle for Location Change */}
                                    <Grid item xs={12}>
                                        <Button
                                            variant="text"
                                            onClick={() => setShowLocationChange(!showLocationChange)}
                                            endIcon={showLocationChange ? <ExpandLess /> : <ExpandMore />}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            {showLocationChange ? 'Ocultar cambio de ubicacion' : 'Cambiar ubicacion (opcional)'}
                                        </Button>
                                    </Grid>

                                    {/* Collapsible Location Selection */}
                                    <Grid item xs={12}>
                                        <Collapse in={showLocationChange}>
                                            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    Solo cambie la ubicacion si el arbol necesita ser movido a otro sitio.
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    {/* Select Canton */}
                                                    <Grid item xs={12} sm={4}>
                                                        <FormControl fullWidth size="small" disabled={!selectedTenant}>
                                                            <InputLabel id="canton-select-label">Canton</InputLabel>
                                                            <Select
                                                                labelId="canton-select-label"
                                                                value={selectedCanton}
                                                                label="Canton"
                                                                onChange={handleCantonChange}
                                                            >
                                                                <MenuItem value="">
                                                                    <em>Seleccionar...</em>
                                                                </MenuItem>
                                                                {filteredCantons.map((c, index) => (
                                                                    <MenuItem key={`canton-${c.slug || c.id || index}`} value={c.slug || ''}>
                                                                        {c.name || c.nombre || 'Sin nombre'}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>

                                                    {/* Select Parish */}
                                                    <Grid item xs={12} sm={4}>
                                                        <FormControl fullWidth size="small" disabled={!selectedCanton}>
                                                            <InputLabel id="parish-select-label">Parroquia</InputLabel>
                                                            <Select
                                                                labelId="parish-select-label"
                                                                value={selectedParish}
                                                                label="Parroquia"
                                                                onChange={handleParishChange}
                                                            >
                                                                <MenuItem value="">
                                                                    <em>Seleccionar...</em>
                                                                </MenuItem>
                                                                {parishes.map((p, index) => (
                                                                    <MenuItem key={`parish-${p.id || index}`} value={p.id || ''}>
                                                                        {p.name || 'Sin nombre'}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>

                                                    {/* Select Site */}
                                                    <Grid item xs={12} sm={4}>
                                                        <FormControl fullWidth size="small" disabled={!selectedParish}>
                                                            <InputLabel id="site-select-label">Sitio</InputLabel>
                                                            <Select
                                                                labelId="site-select-label"
                                                                value={selectedSite}
                                                                label="Sitio"
                                                                onChange={(e) => setSelectedSite(e.target.value || '')}
                                                            >
                                                                <MenuItem value="">
                                                                    <em>Seleccionar...</em>
                                                                </MenuItem>
                                                                {sites.map((s, index) => (
                                                                    <MenuItem key={`site-${s.id_s || index}`} value={s.id_s || ''}>
                                                                        {s.nombre || 'Sin nombre'}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Collapse>
                                    </Grid>

                                    {/* Submit Button */}
                                    <Grid item xs={12}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            fullWidth
                                            startIcon={assigning ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                            onClick={handleAssign}
                                            disabled={assigning || (!selectedTenant && !selectedSite)}
                                            sx={{ mt: 1, py: 1.5 }}
                                        >
                                            {assigning ? 'Reasignando...' : 'Reasignar Arbol'}
                                        </Button>
                                    </Grid>

                                </Grid>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}
        </Box>
    )
}

export default TreeReassignment
